import React, { createContext, ReactNode, useContext, useState } from 'react';
import { useInvoices } from 'hooks';
import { useInvoicesFiltersOptions } from './useInvoicesFiltersOptions';
import { Invoice } from 'types';

const INVOICES_PER_PAGE = 10;

const filterPlaceholder = { label: '', value: '' };

type FilterValue = { label: string; value: string | number };

export type InvoicesContextValue = {
  invoices: Invoice[];
  invoicesLoading: boolean;
  periodOptions: FilterValue[];
  campaignOptions: FilterValue[];
  periodFilter: FilterValue;
  campaignFilter: FilterValue;
  onPeriodFilterChange: (value: FilterValue) => void;
  onCampaignFilterChange: (value: FilterValue) => void;
  filtersApplied: boolean;
  resetFilters: () => void;
  pagination: {
    page: number;
    count: number;
    onPageChange: (page: number) => void;
  };
};

export const defaultContextValue = {
  invoices: [],
  invoicesLoading: false,
  periodOptions: [],
  campaignOptions: [],
  periodFilter: filterPlaceholder,
  campaignFilter: filterPlaceholder,
  onPeriodFilterChange: () => {},
  onCampaignFilterChange: () => {},
  filtersApplied: false,
  resetFilters: () => {},
  pagination: {
    page: 1,
    count: 0,
    onPageChange: () => {},
  },
};

const InvoicesContext = createContext<InvoicesContextValue>(defaultContextValue);

export const useInvoicesContext = () => useContext(InvoicesContext);

export const InvoicesProvider = ({ children }: { children: ReactNode }) => {
  const { periodOptions, campaignOptions, periodDefaultOption, campaignDefaultOption } = useInvoicesFiltersOptions();
  const [periodFilter, setPeriodFilter] = useState<FilterValue>(periodDefaultOption);
  const [campaignFilter, setCampaignFilter] = useState<FilterValue>(campaignDefaultOption);
  const [page, setPage] = useState(1);

  const { getInvoicesQuery } = useInvoices({
    period: periodFilter.value !== periodDefaultOption.value ? (periodFilter.value as string) : null,
    campaignId: campaignFilter.value !== campaignDefaultOption.value ? (campaignFilter.value as number) : null,
    offset: page * INVOICES_PER_PAGE - INVOICES_PER_PAGE,
  });

  const { data: invoicesQueryData, isLoading: invoicesLoading } = getInvoicesQuery;

  const invoices = invoicesQueryData?.invoices ?? [];
  const totalInvoices = invoicesQueryData?.total ?? 0;

  const onPeriodFilterChange = (newValue: FilterValue) => setPeriodFilter(newValue);
  const onCampaignFilterChange = (newValue: FilterValue) => setCampaignFilter(newValue);
  const resetFilters = () => {
    const defaultPeriod = periodOptions[0];
    const defaultCampaign = campaignOptions[0];
    if (defaultPeriod) onPeriodFilterChange(defaultPeriod);
    if (defaultCampaign) onCampaignFilterChange(defaultCampaign);
  };
  const onPageChange = (page: number) => {
    setPage(page);
  };

  return (
    <InvoicesContext.Provider
      value={{
        invoices,
        invoicesLoading,
        pagination: {
          page,
          count: Math.ceil(totalInvoices / INVOICES_PER_PAGE),
          onPageChange,
        },
        periodOptions,
        campaignOptions,
        periodFilter,
        campaignFilter,
        onPeriodFilterChange,
        onCampaignFilterChange,
        filtersApplied:
          periodFilter.value !== periodDefaultOption.value || campaignFilter.value !== campaignDefaultOption.value,
        resetFilters,
      }}
    >
      {children}
    </InvoicesContext.Provider>
  );
};
